<template>
  <tr
    class="hint-statement"
    :class="{ error: statement && statement.errors, inactive: statement && !statement.active }"
    :title="(statement && statement.errors) ? statement.errors.join(', ') : ''">
    <template v-if="!placeholder">
      <td class="position-drag">☰</td>
      <td>
        <button class="statement-comment" :class="{ active: !!statement.comment }"
                @click="editComment = statement"
                :content="statement.comment ? `Comment: ${statement.comment}` : ''" v-tippy>
          <SvgIcon icon="speech-bubble" :size="12"/>
        </button>
        <Modal v-if="editComment" @close="editComment = null">
          <template v-slot:header>Edit comment</template>
          <template v-slot>
            <input type="text" v-model="editComment.comment" class="comment-input" maxlength="256"
                   @change="madeChanges">
          </template>
        </Modal>
      </td>
      <td>
        <ImpactBar
          @click.native="selectHintStatement(statement)"
          :selected="selectedHintStatement !== null && selectedHintStatement.crc32 === statement.crc32"
          :loading="impactLoading"
          v-if="selectedHint && selectedHint.id in impact && statement.crc32 in impact[selectedHint.id]"
          :real="impact[selectedHint.id][statement.crc32].real"
          :potential="impact[selectedHint.id][statement.crc32].potential"
          :positive="impact[selectedHint.id][statement.crc32].positive"/>
        <!--        <ImpactBar :real="Math.round(Math.random() * (40))" :potential="Math.round(Math.random() * 100)"/>-->

        <!--        <span-->
        <!--          v-if="selectedHint && selectedHint.id in impact && statement.crc32 in impact[selectedHint.id]"-->
        <!--          :title="`Real: ${impact[selectedHint.id][statement.crc32].real}\nPotential: ${impact[selectedHint.id][statement.crc32].potential}`">-->
        <!--            {{ impact[selectedHint.id][statement.crc32].real }} / {{ impact[selectedHint.id][statement.crc32].potential }}-->
        <!--        </span>-->
      </td>
      <td class="function-name" v-if="hintFunction && hintFunction.name">
        <SvgIcon v-if="statement.errors" class="fill-red" icon="error" :size="12"/>
        <select v-if="availableMappings.length" @change="changeFunction" class="function-map-dropdown">
          <option :value="hintFunction.id">{{ hintFunction.name }}</option>
          <option v-for="map in availableMappings" :key="`map-${i}-${map.id}}`" :value="map.id">{{ map.name }}</option>
        </select>
        <span v-else>{{ hintFunction.name }}</span>
      </td>
      <td class="function-args">
        <!-- TODO: show icon for errors, clicking opens dialog -->
        <!--                <template v-if="statement.errors">-->
        <!--                    <span class="statement-error" v-for="(error, i) in statement.errors" :key="'error-' + i">{{ error }}</span>-->
        <!--                </template>-->
        <template v-if="hintFunction && hintFunction.args">
          <div class="function-arg" v-for="(functionArg, i) in hintFunction.args" :key="'arg-' + functionArg.name"
               :class="{ error: (statement.args && !!(statement.args[i].error)) }">
            <template v-if="hintFunction.group !== 'python'">
              <AutoComplete
                v-if="functionArg.type === 'attr' || functionArg.type === 'attr4' || (functionArg.type === 'list' && (functionArg.valid_list_type === 'attr' || functionArg.valid_list_type === 'attr4'))"
                v-model="statement.args[i].arg"
                :id="'autocomplete-' + statement.position + '-' + i"
                :type="(functionArg.type === 'attr' || functionArg.type === 'attr4') ? 'single' : 'list'"
                @change="madeChanges"
                className="function-arg"
                :placeholder="functionArg.name"
                :input="statement.args[i].arg"
                :dictionary="functionArg.type === 'attr' ? attr : attr4"/>
              <input v-else-if="functionArg.type === 'list'" :placeholder="functionArg.name" @change="madeChanges"
                     v-model="statement.args[i].arg" :type="'text'">
              <input v-else :placeholder="functionArg.name" @change="madeChanges" v-model="statement.args[i].arg"
                     :type="(functionArg.type === 'text' || functionArg.type === 'xpath' || typeof functionArg.type == 'object') ? 'text' :
                            (functionArg.type === 'int' ? 'number' : '')">
            </template>
            <PythonButton v-else @click.native="pythonStatement = statement"/>

            <div class="python-editor-container" v-if="pythonStatement">
              <div class="backdrop" @click="closePython"></div>
              <div class="python-editor">
                <div class="header">Python Editor</div>
                <div class="content">
                  <div id="python-editor"></div>
                </div>
                <div class="footer">
                  <button @click="closePython" class="button">Cancel</button>
                  <button @click="savePython" class="button">Save</button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </td>
      <td>
        <!--        <input type="checkbox" v-model="statement.active" @change="madeChanges">-->

        <Checkbox v-model="statement.active" :id="`active-${i}`" @change="madeChanges"/>
      </td>
      <td>
        <button class="button button-icon" @click="deleteHintStatement" content="Delete hint statement"
                v-tippy="{ placement: 'left' }">&#x274C;&#xFE0E;
        </button>
      </td>
    </template>
    <template v-else>
      <!-- TODO: This needs to be its own component... -->
      <td></td>
      <td class="function-name" colspan="3">
        <div class="autocomplete-container">
          <input ref="functionName" type="text" placeholder="Enter function name" v-model="functionName"
                 @keydown="autoCompleteKeyDown">
          <div id="autocomplete-functions" v-show="autoComplete.length" class="autocomplete"
               @mouseleave="setHoverIndex(null)">
            <ul>
              <li v-for="(hf, i) in autoComplete" :key="hf.id" :class="{ selected: hoverIndex === i }"
                  @mouseenter="setHoverIndex(i)" @click="addHintStatement(hf)">
                <span>{{ hf.name }}</span>
              </li>
            </ul>
          </div>
        </div>
      </td>
    </template>
  </tr>
</template>

<script>
import { mapState } from 'vuex'
import AutoComplete from './AutoComplete'
import { Events } from '../events'
import SvgIcon from './SvgIcon'
import Modal from './Modal'

import * as ace from 'brace'
import 'brace/mode/python'
import 'brace/theme/tomorrow_night'
import ImpactBar from './ImpactBar'
import PythonButton from './PythonButton'
import { debug } from '../util/log'
import Checkbox from './form/Checkbox'

const attr = [
  'supheader',
  'title',
  'subheader',
  'summary',
  'text',
  'section_header',
  'image',
  'imagetext',
  'author',
  'pubdate',
  'video'
]
const attr4 = ['all', 'title', 'summary', 'text', 'section_header', 'image', 'imagetext', 'author', 'pubdate', 'video']

/**
 * TODO: This component is extremely disgusting, apologies to everyone who sees it. Would be nice to fix it.
 * TODO: Move logic out of template to computed properties/methods.
 * TODO: Split into several vue components where appropriate.
 */

export default {
  name: 'HintStatement',
  components: { Checkbox, PythonButton, ImpactBar, AutoComplete, SvgIcon, Modal },
  data: () => ({
    hintFunction: {},
    functionName: '',
    autoComplete: [],

    hoverIndex: null,

    editComment: null,  // edit comment dialog

    pythonStatement: null,
    pythonCode: '',
    pythonEditor: null,

    attr,
    attr4,
  }),
  props: {
    i: {
      type: Number,
      required: false
    },
    statement: Object,
    placeholder: {
      type: Boolean,
      default: false
    },
  },
  methods: {
    changeFunction(e) {
      const id = e.target.value
      if (!(id in this.hintFunctions)) {
        return
      }
      this.hintFunction = this.hintFunctions[id]
      this.statement.function_id = id

      Events.$emit('madeChanges')
    },
    selectHintStatement(statement) {
      if (this.selectedHintStatement === statement) {
        this.$store.commit('hint/setSelectedStatement', null)
        // OHMRepository.getStatementArticleImpact()
        this.$store.commit('hint/setArticleImpact', {})
      } else {
        this.$store.commit('hint/setSelectedStatement', statement)
        debug("Getting statement article impact...")
        const id_links = this.articles.map(article => article.id_link)

        this.$store.dispatch('hint/getArticleImpact', { id_site: this.site.id_site, crc32: statement.crc32, id_links })
      }
    },
    closePython() {
      this.pythonStatement = null
      this.pythonCode = ''
    },
    savePython() {
      if (this.pythonStatement != null) {
        this.pythonStatement.args = []
        let spl = this.pythonCode.split("\n")
        for (let i = 0; i < spl.length; i++) {
          this.pythonStatement.args.push({
            arg: spl[i]
          })
        }
      }
      Events.$emit('madeChanges')
      this.closePython()
    },
    madeChanges() {
      Events.$emit('madeChanges')
    },
    setHoverIndex(i) {
      this.hoverIndex = i
    },
    matchFunction() {
      // Find the function which has our statement's function_id
      if (this.statement && 'function_id' in this.statement) {
        this.hintFunction = this.hintFunctions[this.statement.function_id]
      }
    },
    addHintStatement(hf) {
      Events.$emit('addHintStatement', {
        hintFunction: hf
      })

      this.functionName = ''
      this.autoComplete = []
    },
    deleteHintStatement() {
      Events.$emit('deleteHintStatement', {
        position: this.statement.position
      })
    },
    autoCompleteKeyDown(e) {
      let cur
      switch (e.keyCode) {
        case 27: // ESC
          this.functionName = ''
          this.autoComplete = []
          break
        case 13: // Enter
          if (this.hoverIndex != null) {
            this.addHintStatement(this.autoComplete[this.hoverIndex])
          }
          break
        case 40: // KeyDown
          if (this.hoverIndex == null) {
            this.hoverIndex = 0
          } else if (this.hoverIndex === this.autoComplete.length - 1) {
            this.hoverIndex = 0
          } else {
            this.hoverIndex++
          }
          cur = document.getElementById('autocomplete-functions').childNodes[0].childNodes[this.hoverIndex]
          if (cur) {
            document.getElementById('autocomplete-functions').scroll(0, cur.offsetTop)
          }
          break
        case 38: // KeyUp
          if (this.hoverIndex === 0) {
            this.hoverIndex = this.autoComplete.length - 1
          } else {
            this.hoverIndex--
          }
          cur = document.getElementById('autocomplete-functions').childNodes[0].childNodes[this.hoverIndex]
          if (cur) {
            document.getElementById('autocomplete-functions').scroll(0, cur.offsetTop)
          }
          break
        case 32: // Space
          if (e.ctrlKey) {
            if (!this.functionName.length && this.autoComplete.length) {
              this.autoComplete = []
            } else {
              this.autoCompleteSearch(e.target, this.functionName, true)
            }
          }
          break
      }
    },
    autoCompleteSearch(element, search, acceptEmpty) {
      if (!acceptEmpty && !search.trim().length) {
        this.autoComplete = []
        return
      }

      let caseSensitive = false

      for (let i = 0; i < search.length; i++) {
        let char = search.charAt(i)
        // We want a case insensitive search, unless one of the input characters are capitalized
        if (char.toUpperCase() === char) {
          caseSensitive = true
        }
      }

      let reg = ""
      for (let i = 0; i < search.length; i++) {
        let char = search.charAt(i)

        char = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        if (!caseSensitive) {
          char = char.toLowerCase()
        }
        reg += "(.*)" + char
      }
      reg += "(.*)"
      reg = new RegExp(reg)

      let matched = []

      for (let i = 0; i < Object.keys(this.hintFunctions).length; i++) {
        const key = Object.keys(this.hintFunctions)[i]
        let val = this.hintFunctions[key].name

        let oclVers = this.hintFunctions[key].ocl_versions.split(',')
        let found = false
        oclVers.forEach(ver => {
          if (parseInt(ver) === this.site.ocl_version) {
            found = true
          }
        })

        if (!found) {
          continue
        }

        let search_val = val
        if (!caseSensitive) {
          search_val = val.toLowerCase()
        }

        let search = search_val.search(reg)
        if (search !== -1) {
          matched.push(this.hintFunctions[key])
        }
      }

      let rect = element.getBoundingClientRect()
      let bodyHeight = document.body.clientHeight

      this.autoComplete = matched

      // Check if we have enough space in body to place the autocomplete box _under_ the input.
      // If not, assume we have enough place to place it above.
      // TODO: Fix
      const autocompleteHeight = document.getElementById('autocomplete-functions').clientHeight
      if ((rect.top + autocompleteHeight) <= (bodyHeight - 50)) {
        // document.getElementById('autocomplete-functions').style.top = (rect.top + rect.height + 5) + "px"
      } else {
        // document.getElementById('autocomplete-functions').style.top = (rect.top - autocompleteHeight) + "px"
      }

    }
  },
  watch: {
    functionName(val) {
      this.autoCompleteSearch(this.$refs.functionName, val, false)
      if (this.autoComplete.length) {
        this.hoverIndex = 0
        document.getElementById('autocomplete-functions').scroll(0, 0)
      } else {
        this.hoverIndex = null
      }
    },
    pythonStatement(val) {
      if (val) {
        const len = Object.keys(val.args).length
        let full_args = ""

        for (let i = 0; i < len; i++) {
          full_args += val.args[i].arg
          if (i !== (len - 1)) {
            full_args += "\n"
          }
        }
        this.pythonCode = full_args

        // TODO: replace python editor stuff with new TextEditor component

        setTimeout(() => {
          const editor = ace.edit('python-editor')
          editor.getSession().setMode('ace/mode/python')
          editor.setTheme('ace/theme/tomorrow_night')
          editor.setValue(this.pythonCode, -1)

          editor.on('change', () => {
            this.pythonCode = editor.getValue()
          })
        }, 50)

      }
    },
    hintFunctions: {
      deep: true,
      handler() {
        this.matchFunction()
      }
    },
    statement: {
      immediate: true,
      deep: true,
      handler() {
        this.matchFunction()
      }
    },
    selectedHint() {
      this.functionName = ''
    }
  },
  computed: {
    ...mapState('article', {
      articles: state => state.latestArticles,
    }),
    ...mapState('hint', {
      hintFunctions: state => state.hintFunctions,
      hintFunctionMap: state => state.hintFunctionMap,
      impact: state => state.impact,
      impactLoading: state => state.impactLoading,
      selectedHint: state => state.selected,
      selectedHintStatement: state => state.selectedStatement,
    }),
    ...mapState('site', {
      site: state => state.data
    }),
    availableMappings() {
      if (!this.hintFunction) {
        return []
      }

      const filtered = []
      Object.keys(this.hintFunctionMap).forEach(i => {
        if (this.hintFunctionMap[i]['from_id'] === this.hintFunction.id) {
          filtered.push(this.hintFunctions[this.hintFunctionMap[i]['to_id']])
        }
      })

      return filtered
    }
  }
}
</script>

<style lang="sass">
@use '../styles/variables' as v

.autocomplete-container
  position: relative

  .autocomplete
    position: absolute
    width: 300px
    max-height: 100px
    background: v.$light-black3
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.6)
    overflow-y: auto
    line-height: initial

    ul
      li
        cursor: pointer

        &.selected
          background: v.$light-black

        span
          display: inline-block
          padding: 2px 6px


.python-editor-container
  position: fixed
  width: 100%
  height: 100%
  top: 0
  left: 0
  z-index: 9000
  line-height: initial

  .backdrop
    z-index: 1500
    background: rgba(0, 0, 0, 0.4)
    width: 100%
    height: 100%

  .python-editor
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)
    z-index: 2000
    width: 1200px
    height: 800px
    max-height: 90%
    max-width: 100%
    background: v.$ui-background
    border: v.$ui-border
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5)
    display: grid
    grid-template-rows: auto 1fr auto

    .header
      color: v.$white

    .header,
    .content,
    .footer
      padding: 10px 20px

    .header
      border-bottom: 1px solid v.$light-black2
      font-size: v.$font-size-big

    .footer
      display: flex
      flex-wrap: wrap
      justify-content: flex-end

      button
        margin-left: 10px

    .content
      position: relative

#python-editor
  height: 100%
  width: 100%
  position: absolute
  left: 0
  top: 0
  font-size: 14px

.hint-statement
  align-items: center
  color: v.$white
  font-family: v.$font-family-monospace
  font-size: v.$font-size
  line-height: 6px

  td
    white-space: nowrap
    padding: 0 5px

    &:last-child
      text-align: right

  &:hover
    background: #252525

  &.error
    border-top: 1px dashed v.$red
    border-bottom: 1px dashed v.$red

  .position-drag
    color: v.$gray
    cursor: grab
    text-align: center

  .function-name
    text-align: right
    padding-right: 10px

  .function-map-dropdown
    background: none
    border: none
    color: v.$dark-white
    appearance: none
    height: v.$font-size
    line-height: v.$font-size
    position: relative
    right: -4px
    text-align: right
    cursor: pointer

    &:hover
      color: v.$yellow

  button.btn
    padding: 0

  input[type="text"],
  input[type="number"]
    background: none
    color: v.$white
    font-family: v.$font-family-monospace
    font-size: v.$font-size
    border: none
    border-top: 1px solid transparent
    border-bottom: 1px solid transparent

    &:focus
      outline: none
      border-bottom: 1px solid v.$gray3

  .comment-input
    border-bottom: 1px solid v.$gray3 !important
    padding: 4px 6px
    width: 100%

  .statement-comment
    cursor: pointer
    border: none
    padding: 0
    margin: 0
    background: none

    .svg-icon
      fill: v.$white
      opacity: 0.4

    &.active .svg-icon
      fill: v.$yellow
      opacity: 1

  .function-args
    display: flex
    flex-wrap: nowrap

    .statement-error
      white-space: nowrap
      margin: auto 0

    .function-arg
      margin-right: 10px

      input[type="text"]
        min-width: 250px
        width: 350px

      input[type="number"]
        min-width: 80px

      &.error
        border: 1px dashed v.$red

</style>
